import { useState, useEffect, useContext, useRef } from "react";
import { useParams } from "react-router-dom";
import { useRecoilState, useRecoilValue } from "recoil";
import { currentPostAtom, currentSlideAtom } from "../../atom";
import { Button } from "antd";
import { createOpenSeadragon } from "./Custom-OpenSeadragon";
import { getSlideMpp } from "../../api/deepzoomAPI";
import { getSlideData, postAnnotation } from "../../api/slideAPI";
import { ViewerContext } from "../../page/Viewer";
import { ViewerToggleManager } from "../button";
import { patchUserCustomSetting } from "../../api/user";
import { useAnnotationInfo } from "../../hooks/useAnnotationInfo";
import { AnnotationInfoPanel as AnnotationInfoPanelComponent } from "./AnnotationInfoPanel";
import ViewerContextMenu from "./ViewerContextMenu";
import styled from "styled-components";

const Openseadragon = () => {
  const { userCustomSet, setUserCustomSet, setActivePanel } = useContext(ViewerContext);
  const [viewer, setViewer] = useState<any>(null);
  const [icc, setIcc] = useState("none");
  const [currentSlide, setCurrentSlide] = useRecoilState(currentSlideAtom);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // 어노테이션 정보 훅 사용
  const annotationInfo = useAnnotationInfo({ currentSlide, setCurrentSlide });

  const currentPost = useRecoilValue(currentPostAtom);
  const { slide } = useParams();

  const changeColor = () => {
    if (icc === "none") setIcc("GT450");
    else setIcc("none");
  };

  const getAnnotationList = async () => {
    if (userCustomSet?.useAnnotation) {
      const data = await getSlideData(currentSlide.slideId);
      setCurrentSlide(data);
    } else {
      // 어노테이션 비활성화 시 빈 배열로 설정
      setCurrentSlide((prev: any) => ({ ...prev, annotations: [] }));
      return null;
    }
  };

  const saveAnnotation = async (
    annotationPath: any,
    annotationName: any,
    rulerValue: any,
  ) => {
    if (rulerValue)
      await postAnnotation(
        currentSlide.slideId,
        annotationPath,
        `${annotationName} / (${rulerValue}mm)`,
      );
    else
      await postAnnotation(
        currentSlide.slideId,
        annotationPath,
        annotationName,
      );
  };

  const getMpp = async () => {
    return await getSlideMpp(currentPost.id, slide || "");
  };

  // 훅에서 가져온 handleAnnotationClick 사용
  const handleAnnotationClickWrapper = (event: any) => {
    annotationInfo.handleAnnotationClick(event);
  };

  useEffect(() => {
    if (slide && viewer) {
      console.log('Opening slide:', slide);
      viewer.open(
        process.env.REACT_APP_DEEPZOOM_URL +
          `/${icc}/${currentPost.id}/${slide}` +
          ".dzi",
      );

      // 줌 변경 시 정보창 닫기 및 선택 해제
      const handleZoom = () => {
        annotationInfo.closeAnnotationInfo();
      };

      viewer.addHandler('zoom', handleZoom);
      viewer.addHandler('pan', handleZoom);

      return () => {
        viewer.removeHandler('zoom', handleZoom);
        viewer.removeHandler('pan', handleZoom);
      };
    }
  }, [viewer, icc, slide]);

  useEffect(() => {
    viewer?.destroy();
    setViewer(
      createOpenSeadragon({
        showScalebar: true,
        useScaleBar: userCustomSet ? userCustomSet?.useScaleBar : true,
        useMagnificationBar: userCustomSet
          ? userCustomSet?.useMagnificationBar
          : true,
        useRotationSlider: userCustomSet
          ? userCustomSet.useViewerRotationSlider
          : true,
        useFiltering: userCustomSet ? userCustomSet.useGammaSlider : true,
        showNavigator: userCustomSet ? userCustomSet.useViewerThumbnail : true,
        useCapture: userCustomSet ? userCustomSet.useCaptureButton : true,
        saveAnnotation: true,
        getSlideMpp: getMpp,
        getAnnotations: getAnnotationList,
        addAnnotation: saveAnnotation,
        annotationClickEvent: handleAnnotationClickWrapper,
        useInvertedZoom: userCustomSet ? userCustomSet.useInvertedZoom : false,
      }),
    );
  }, [icc, userCustomSet?.useScaleBar, userCustomSet?.useMagnificationBar, userCustomSet?.useViewerRotationSlider, userCustomSet?.useGammaSlider, userCustomSet?.useViewerThumbnail, userCustomSet?.useCaptureButton, userCustomSet?.useAnnotation, userCustomSet?.useInvertedZoom ]);

  useEffect(() => {
    if (viewer) {
      if (currentSlide?.annotations && currentSlide.annotations.length > 0) {
        const pathList = [];
        for (const path of currentSlide.annotations) {
          if (!path.annoInfo) continue;

          const annoInfo = JSON.parse(JSON.stringify(path.annoInfo));
          if (annoInfo && Array.isArray(annoInfo) && annoInfo[1]) {
            annoInfo[1].id = `annotation${path.annoId}`;
            annoInfo[1].label = path.content;
            pathList.push(annoInfo);
          }
        }
        viewer.setAnnotation(pathList);
      } else {
        // 어노테이션이 없으면 빈 배열로 설정
        viewer.setAnnotation([]);
      }
    }
  }, [currentSlide, viewer]);

  // userGammaValue가 변경될 때 OSD 필터 및 슬라이더 업데이트
  useEffect(() => {
    if (!viewer || !userCustomSet?.useGammaSlider) return;

    const gammaValue = userCustomSet?.userGammaValue ?? 1;
    viewer.setGammaValue?.(gammaValue);
  }, [viewer, userCustomSet?.userGammaValue, userCustomSet?.useGammaSlider]);

  // 감마 슬라이더 이벤트 리스너 추가 (debounce 적용)
  useEffect(() => {
    if (!viewer) return;

    viewer.onGammaInput = (newValue: number) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(async () => {
        try {
          const updatedSettings = { ...userCustomSet, userGammaValue: newValue };
          setUserCustomSet(updatedSettings);
          await patchUserCustomSetting(updatedSettings);
          console.log(`Gamma value updated to ${newValue}`);
        } catch (error) {
          console.error('Failed to update gamma value:', error);
        }
      }, 1500);
    };

    return () => {
      viewer.onGammaInput = null;
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [viewer, userCustomSet, setUserCustomSet]);

  const onReset = () => {
    // toggle menu 상태 초기화
    setUserCustomSet({
      useScaleBar: true,
      useMagnificationBar: true,
      useCaptureButton: true,
      useViewerRotationSlider: true,
      useGammaSlider: true,
      useViewerThumbnail: true,
      useAnnotation: true,
      useICCButton: true,
      userGammaValue: 1,
      useInvertedZoom: false,
    });

    // slide viewer 상태 초기화
    viewer?.destroy();
    setViewer(
      createOpenSeadragon({
        showScalebar: true,
        useRotationSlider: true,
        useFiltering: true,
        showNavigator: true,
        useCapture: true,
        saveAnnotation: true,
        getSlideMpp: getMpp,
        getAnnotations: getAnnotationList,
        addAnnotation: saveAnnotation,
        annotationClickEvent: handleAnnotationClickWrapper,
        useInvertedZoom: false,
      }),
    );

    // ICC 상태 초기화
    setIcc("none");
  };

  const onSubmit = async () => {
    await patchUserCustomSetting(userCustomSet);
  };


  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      <ViewerContextMenu viewer={viewer} icc={icc} onChangeIcc={changeColor}>
        <StOpenseadragon
          id="openseadragon"
          $selectedAnnotation={annotationInfo.selectedAnnotation?.annoId}
        />
      </ViewerContextMenu>

      {/* 어노테이션 정보창 컴포넌트 - 어노테이션 정보 창 핸들링 */}
      <AnnotationInfoPanelComponent {...annotationInfo} />

      {userCustomSet?.useICCButton && (
        <StyledICCButton
          onClick={changeColor}
          $isActive={icc !== "none"}
        >
          ICC
        </StyledICCButton>
      )}
      <ViewerToggleManager />
      <div style={{
        position: "absolute",
        bottom: "10px",
        right: "10px",
        display: "flex",
        gap: "8px",
        zIndex: 1000
      }}>
        <ViewerToggleManager />
        {/* <Button onClick={onReset}>Reset</Button>
        <Button onClick={onSubmit}>설정 저장</Button> */}
      </div>
    </div>
  );
};

export default Openseadragon;

interface StyledICCButtonProps {
  $isActive: boolean;
}

const StyledICCButton = styled(Button)<StyledICCButtonProps>`
  position: absolute;
  // top: 10px;
  // right: 30.3em;
  // font-size: 0.875rem !important;
  // height: 36.5px !important;
  // padding: 9.25px 18px !important;
  top: 5px;
  right: 30.8em;
  font-size: 0.6rem !important;
  padding: 0 0.4rem;
  height: 1.45rem;
  background-color: ${props => props.$isActive ? 'rgba(24, 144, 255, 0.7)' : 'rgba(0, 0, 0, 0.5)'} !important;
  color: white !important;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
  border-radius: 4px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;

  &:hover {
    background-color: ${props => props.$isActive ? 'rgba(24, 144, 255, 0.9)' : 'rgba(0, 0, 0, 0.8)'} !important;
    border-color: rgba(255, 255, 255, 0.5) !important;
  }
`;

interface StOpenseadragonProps {
  $selectedAnnotation?: number;
}

const StOpenseadragon = styled('div')<StOpenseadragonProps>(({ theme, $selectedAnnotation }) => ({
  width: '100%',
  height: '100%',

  '& .annotation': {
    cursor: 'pointer'
  },

  [`& #annotation${$selectedAnnotation}`]: {
    strokeWidth: '4 !important',
    filter: 'brightness(0.85) drop-shadow(0 0 6px currentColor) drop-shadow(0 0 12px currentColor)',

    // 화살표(g 요소)의 자식 line, path도 동일 효과
    '& line': {
      strokeWidth: '4 !important'
    },
    '& path': {
      strokeWidth: '4 !important'
    }
  },

  '& .osd-annotation-label-bg': {
    pointerEvents: 'none',
  },

  '& .osd-annotation-label': {
    fill: 'white !important',
    stroke: 'rgba(0, 0, 0, 0.8) !important',
    pointerEvents: 'none',
    userSelect: 'none',
    paintOrder: 'stroke fill',
    fontWeight: '400 !important',
    fontFamily: '"Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important',
  }
}));
